import React, { useRef, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import { set, uniq } from 'lodash';
import { withStyles } from '@glu/theming';
import locale from '@glu/locale';
import { FormContext } from '@glu/validation-react';
import { Button, SMALL, SECONDARY } from '@glu/buttons-react';
import BrandingContext from '../../BrandingContext';

import styles from './Footer.styles';
import exportTheme from '../../utility/exportTheme';

const flattenChildren = children => children.reduce((arr, child) => {
  if (child.children) {
    return [...arr, ...flattenChildren(child.children)];
  }
  if (child.exclude) {
    return arr;
  }
  if (child.urlName) {
    return [...arr, child, { name: child.urlName, type: 'fontUrls' }];
  }
  return [...arr, child];
}, []);

const Footer = ({ classes, parts }) => {
  const { data } = useContext(FormContext);
  const { setBrandingFromUpload } = useContext(BrandingContext);
  const fileRef = useRef();

  const handleUploadClick = useCallback((event) => {
    event.preventDefault();

    fileRef.current.click();
  }, []);


  const handleDownload = useCallback(
    (event) => {
      event.preventDefault();

      const editableArray = flattenChildren(parts);
      const downloadTheme = editableArray.reduce((newTheme, { name, type }) => {
        const themeValue = data[name];
        if (type === 'fontUrls') {
          set(newTheme, name, uniq(themeValue)); // eliminate duplicates from this array
        } else {
          set(
            newTheme,
            name,
            (type === 'pixel' || type === 'fontWeight') ? parseInt(themeValue, 10) : themeValue
          );
        }
        return newTheme;
      }, {});
      exportTheme(downloadTheme);
    },
    [data, parts]
  );

  const handleUpload = useCallback((event) => {
    const { files: [jsonFile] } = event.target;
    const reader = new FileReader();
    reader.onload = (fileEvent) => {
      setBrandingFromUpload(JSON.parse(fileEvent.target.result));
    };
    reader.readAsText(jsonFile);
  }, [setBrandingFromUpload]);

  return (
    <div className={`${classes.root} branding-footer`}>
      <Button onClick={handleDownload} size={SMALL}>{locale.get('branding.downloadTheme')}</Button>
      <Button onClick={handleUploadClick} size={SMALL} variant={SECONDARY}>{locale.get('branding.uploadTheme')}</Button>
      <input ref={fileRef} type="file" onChange={handleUpload} />
    </div>
  );
};

Footer.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  parts: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    labelText: PropTypes.string.isRequired,
    children: PropTypes.arrayOf(PropTypes.shape({}))
  })).isRequired
};

export default withStyles(styles)(Footer);
